import type { ReactNode } from "react";
/* 絵は束ねた入口から読まない。後から読むヘッダーの絵まで root の読み込みに残る */
import { KameArukuIcon } from "../base/Icon/KameArukuIcon";
import { Button } from "../base/Button";
import {
  ArticleCardList,
  DefaultErrorPageLink,
  DestinationList,
  ErrorPageShell,
  ErrorPageSocialLinks,
  type ErrorPageDestination,
  type ErrorPageLinkComponent,
} from "./ErrorPageParts";

export interface ServerErrorPageProps {
  /**
   * カスタムLinkコンポーネント（React Router等）
   */
  LinkComponent?: ErrorPageLinkComponent;

  /**
   * 追加のクラス名
   */
  className?: string;

  /**
   * 画面に出す HTTP ステータス。
   *
   * **返しているコードと同じ数字を出す。** 2026-09-03、フェイルクローズで
   * 503 を返しているのに画面が「500」と描いていて、切り分けを誤らせた。
   * curl は 503、画面は 500 で、どちらの数字で調べるかが分かれる。
   *
   * 既定が 500 なのは、素の例外にはステータスが無いため。
   */
  status?: number;

  /** 問い合わせのときに読み上げてもらう番号。ログと Sentry で同じ値を引ける */
  errorRef?: string | null;

  /** API が落ちていても開ける見込みのある行き先 */
  destinations?: readonly ErrorPageDestination[];

  /** 画面の頭に置くサイトのヘッダー。ログイン状態を知るのは呼び出し側 */
  header?: ReactNode;

  /** 研究所の記事。API が落ちていても出せるよう、呼び出し側が静的に持つ */
  articles?: readonly ErrorPageDestination[];

  /** 既定はページの再読み込み */
  onReload?: () => void;

  /** 場面の絵の住所。絵の中に 500 を描いてあるので、status が 500 のときだけ使う */
  artSrc?: string;
}

export function ServerErrorPage({
  LinkComponent = DefaultErrorPageLink,
  className = "",
  status = 500,
  errorRef,
  destinations = [],
  onReload,
  header,
  articles = [],
  artSrc,
}: ServerErrorPageProps) {
  return (
    <ErrorPageShell
      code={status}
      header={header}
      className={className}
      /* 絵の中の数字は 500。503 などのときは絵を使わず、返したコードの数字を出す */
      artSrc={status === 500 ? artSrc : undefined}
      mascot={<KameArukuIcon className="h-auto w-[96px] sm:w-[128px]" />}
    >
      <div className="flex flex-col gap-3">
        {/* この見出しは本番の配備後検査が画面の判定に使っている。変えるなら
            apps/client/tests/e2e/prod-cutover/postdeploy-helpers.ts も直す */}
        <h1 className="text-xl font-bold leading-snug text-main">
          サーバーにアクセスできない状態です
        </h1>
        {/* auto-phrase の効かない環境でも「く/ださい」と割れないよう、文ごとに折らない塊にする */}
        <p className="leading-relaxed text-sub">
          <span className="inline-block">一時的にページを表示できませんでした。</span>
          <span className="inline-block">時間を置いて、もう一度読み込んでください。</span>
        </p>
      </div>
      <div className="flex flex-col items-start gap-4">
        <Button
          variant="primary"
          size="md"
          onClick={onReload ?? (() => window.location.reload())}
        >
          もう一度読み込む
        </Button>
        {errorRef && (
          <p className="max-sm:text-[0.9375rem] text-sm leading-relaxed text-sub">
            お問い合わせのときは、次の番号をお伝えください。
            <br />
            <span className="font-mono text-main">エラーID {errorRef}</span>
          </p>
        )}
      </div>
      <DestinationList
        heading="次に行く先"
        items={destinations}
        LinkComponent={LinkComponent}
        listName="destination"
      />
      <ArticleCardList
        heading="研究所の記事"
        items={articles}
        LinkComponent={LinkComponent}
        listName="lab-article"
      />
      <ErrorPageSocialLinks />
    </ErrorPageShell>
  );
}
